// src/components/Admin/PostManager/PostsLogTable.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, TextField, TableContainer, Paper, Table, TableHead,
    TableRow, TableCell, TableBody, CircularProgress, Alert,
    MenuItem, Select, Typography, Pagination, Button,
} from '@mui/material';
import api from '../../../api/axios';
import * as XLSX from 'xlsx';  // ✅ 추가

const actionLabel = {
    soft_delete: '삭제',
    blind: '블라인드',
    unblind: '블라인드 해제',
    edit: '수정',
};

const ROWS_PER_PAGE = 15;

export default function PostsLogTable() {
    const [logs, setLogs] = useState([]);
    const [search, setSearch] = useState('');
    const [actionFilter, setActionFilter] = useState('all');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [page, setPage] = useState(1);

    /* ───────── 데이터 로드 ───────── */
    useEffect(() => {
        api.get('/api/posts/logs/')
            .then((res) => setLogs(res.data.logs || []))
            .catch(() => setError('로그를 불러오는 데 실패했습니다.'))
            .finally(() => setLoading(false));
    }, []);

    /* ───────── 필터링 ───────── */
    const filtered = useMemo(() => (
        logs
            .filter((l) => (actionFilter === 'all' ? true : l.action === actionFilter))
            .filter((l) =>
                (l.post_title || '').toLowerCase().includes(search.toLowerCase())
                || (l.action || '').toLowerCase().includes(search.toLowerCase())
                || (l.memo || '').toLowerCase().includes(search.toLowerCase()))
    ), [logs, search, actionFilter]);

    const totalPages = Math.ceil(filtered.length / ROWS_PER_PAGE) || 1;
    const paginated = filtered.slice(
        (page - 1) * ROWS_PER_PAGE,
        page * ROWS_PER_PAGE,
    );

    // ───────── 엑셀 내보내기 ─────────
    const handleExportExcel = () => {
        // 🔸 현재 필터/검색이 반영된 전체 목록(페이지 나누기 전)
        const rows = filtered.map((l) => ({
            ID: l.id,
            게시글ID: l.post_id,
            게시글제목: l.post_title,
            행동: actionLabel[l.action] || l.action,
            처리자: l.acted_by || '-',
            시간: l.acted_at,          // 이미 문자열로 포맷된 상태
            사유: l.memo || '',
        }));

        if (rows.length === 0) {
            alert('엑셀로 내보낼 로그가 없습니다.');
            return;
        }

        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'PostLogs');

        const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
        XLSX.writeFile(workbook, `post_logs_${today}.xlsx`);
    };

    useEffect(() => setPage(1), [search, actionFilter]);

    /* ───────── 로딩 / 에러 ───────── */
    if (loading) {
        return (
            <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }
    if (error) return <Alert severity="error">{error}</Alert>;

    /* ───────── 렌더 ───────── */
    return (
        <Box
            sx={{
                /* 모바일: 전체 패널 90 % 축소 */
                '@media (max-width:600px)': {
                    transform: 'scale(0.9)',
                    transformOrigin: 'top left',
                },
            }}
        >
            {/* 검색 · 필터 바 */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: 2,
                    flexWrap: 'wrap',
                }}
            >
                <TextField
                    label="게시글/행동/사유"
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ minWidth: 240 }}
                />
                <Select
                    size="small"
                    value={actionFilter}
                    onChange={(e) => setActionFilter(e.target.value)}
                    sx={{ minWidth: 140 }}
                >
                    <MenuItem value="all">전체</MenuItem>
                    <MenuItem value="soft_delete">삭제</MenuItem>
                    <MenuItem value="blind">블라인드</MenuItem>
                    <MenuItem value="unblind">블라인드 해제</MenuItem>
                </Select>
                <Typography variant="body2" color="text.secondary">
                    총&nbsp;
                    {logs.length}
                    개 / 현재&nbsp;
                    {filtered.length}
                    개
                </Typography>
                {/* ✅ 오른쪽으로 밀어붙인 엑셀 버튼 */}
                <Box sx={{ flexGrow: 1 }} />
                <Button
                    variant="outlined"
                    size="small"
                    onClick={handleExportExcel}
                >
                    📥 엑셀로 내보내기
                </Button>
            </Box>

            {/* 테이블 */}
            <TableContainer
                component={Paper}
                variant="outlined"
                sx={{
                    borderRadius: 2,
                    '& tr:hover': { bgcolor: 'grey.100' },
                    '& td, & th': {
                        borderBottom: '1px solid #eee',
                        p: 1.25,
                        whiteSpace: 'nowrap',
                        '@media (max-width:600px)': {
                            p: 0.75,
                            fontSize: '0.8rem',
                        },
                    },
                    '& th': { bgcolor: 'grey.50', fontWeight: 'bold' },
                    /* 모바일: 가로 스크롤 허용 */
                    '@media (max-width:600px)': {
                        width: 'max-content',
                        overflowX: 'auto',
                    },
                }}
            >
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell align="center">ID</TableCell>
                            <TableCell align="left">게시글</TableCell>
                            <TableCell align="center">행동</TableCell>
                            <TableCell align="center">처리자</TableCell>
                            <TableCell align="center">시간</TableCell>
                            <TableCell align="center">사유</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {paginated.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                                    🔍 조건에 맞는 로그가 없습니다.
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginated.map((l) => (
                                <TableRow key={l.id}>
                                    <TableCell align="center">{l.id}</TableCell>
                                    <TableCell align="left">
                                        <a
                                            href={`/main/posts/${l.post_id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ textDecoration: 'none', color: '#1976d2' }}
                                        >
                                            {l.post_title}
                                            {' '}
                                            (#
                                            {l.post_id}
                                            )
                                        </a>
                                    </TableCell>
                                    <TableCell align="center">
                                        {actionLabel[l.action] || l.action}
                                    </TableCell>
                                    <TableCell align="center">{l.acted_by || '-'}</TableCell>
                                    <TableCell align="center">
                                        {new Date(l.acted_at).toLocaleString('ko-KR')}
                                    </TableCell>
                                    <TableCell align="center">{l.memo || '-'}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* 페이지네이션 */}
            {totalPages > 1 && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mt: 3,
                        '@media (max-width:600px)': {
                            transform: 'scale(0.9)',
                        },
                    }}
                >
                    <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, val) => setPage(val)}
                        shape="rounded"
                        showFirstButton
                        showLastButton
                        color="primary"
                    />
                </Box>
            )}
        </Box>
    );
}
